<script lang="ts">
    import { createEventDispatcher } from 'svelte'
    import { getBlockDefinition, getEffectiveBlockWidth } from '../blocks/block-registry'
    import DocumentHeader from '../shared/document-header.svelte'
    import DocumentFooter from '../shared/document-footer.svelte'
    import type { PlacedBlock } from '../types/placed-block'
    import type { DocumentHeaderFooter } from '../types/document-header-footer'
    import type { FillContext } from './context-snapshot'

    /**
     * Componente de preenchimento reutilizável.
     * Quando `hideActions=false` (padrão, ex: link público): exibe botões próprios e despacha
     * eventos `save` (rascunho) e `submit` (concluído) com o documento pronto.
     * Quando `hideActions=true` (ex: tela autenticada): esconde os botões. O componente pai
     * acessa `validate()` e `buildDocument()` via `bind:this` para controlar o envio.
     * `readOnlyAll` trava todos os campos e some com os botões — usado no link público em
     * modo "apenas visualizar" (diferente de `public`, que só trava campos marcados como
     * `readOnlyForPublic` individualmente no builder).
     * `allowDraft=false` esconde apenas o botão "Salvar rascunho" — um documento que já saiu
     * do status de rascunho não pode voltar para ele.
     */
    export let title: string = ''
    export let blocks: PlacedBlock[] = []
    export let headerFooter: DocumentHeaderFooter
    export let initialAnswers: Record<string, any> = {}
    export let saving: boolean = false
    export let hideActions: boolean = false
    export let readOnlyAll: boolean = false
    export let allowDraft: boolean = true
    /** Quando true, o botão final vira "Assinar" — o template pede assinatura ao concluir o
     * preenchimento (ver document-header-footer.ts's DocumentSignature). A validação/dispatch do
     * submit não mudam, só o rótulo: quem decide o que fazer depois é o componente pai. */
    export let signatureRequested: boolean = false
    /** Reexibe só o botão "Assinar" mesmo com readOnlyAll=true — caso de um documento já concluído
     * cuja tentativa de assinatura anterior falhou (ex.: lambda-pdf fora do ar). O conteúdo continua
     * travado (nada de campos editáveis), só a ação de assinar de novo fica disponível. */
    export let showSignButton: boolean = false
    export let context: FillContext = {}
    /** Só é necessário em contexto público (sem usuário logado) — repassado ao DocumentHeader para
     * buscar os dados da empresa via API pública quando o cabeçalho usa "Usar os dados da empresa". */
    export let companyId: string | null = null
    /** ID do formulário salvo — repassado aos blocos que precisam de chamadas API imediatas (ex: tabela dinâmica). */
    export let formId: string | null = null
    let isPublic = false
    export { isPublic as public }

    $: resolvedBlocks = blocks.map((block) => {
        if (block.type !== 'label') return block
        const text = window.api.utils.utils.formsReplaceTags(
            block.data?.text ?? '',
            context.person,
            context.invoice || context.bill,
            context.attendant,
            new Date(),
            context.invoice ? 'invoice' : context.bill ? 'bill' : undefined,
            context.user?.userName,
        )
        return { ...block, data: { ...block.data, text } }
    })

    const dispatch = createEventDispatcher()

    let answers: Record<string, any> = buildInitialAnswers()
    let validationError = ''

    // Svelte 4: safe_not_equal para arrays sempre retorna true, então $: if (blocks) dispararia
    // em QUALQUER re-render do pai (contexto, saving, etc.), resetando as respostas do usuário.
    // Comparar por referência garante que o reset só ocorre quando blocks realmente muda
    // (novo template selecionado ou formulário aberto para edição).
    let _lastBlocks = blocks
    $: if (blocks !== _lastBlocks) {
        _lastBlocks = blocks
        answers = buildInitialAnswers()
        validationError = ''
    }

    function buildInitialAnswers(): Record<string, any> {
        const result: Record<string, any> = {}
        for (const block of blocks) {
            const definition = getBlockDefinition(block.type)
            result[block.id] = initialAnswers[block.id] ?? (definition ? definition.createDefaultValue() : null)
        }
        return result
    }

    function isFieldLocked(block: PlacedBlock) {
        return readOnlyAll || (isPublic && block.readOnlyForPublic)
    }

    function isAnswerEmpty(block: PlacedBlock) {
        const definition = getBlockDefinition(block.type)
        return definition ? definition.isValueEmpty(answers[block.id]) : false
    }

    export function buildDocument(status: 'draft' | 'submitted') {
        return {
            templateSnapshot: {
                title,
                headerFooter,
                blocks: blocks.map((block) => ({
                    id: block.id,
                    type: block.type,
                    label: block.label,
                    data: block.data,
                    required: block.required,
                    readOnlyForPublic: block.readOnlyForPublic,
                    width: getEffectiveBlockWidth(block),
                })),
            },
            answers,
            status,
        }
    }

    export function validate(): boolean {
        const missing = blocks.filter((b) => b.required && !isFieldLocked(b) && isAnswerEmpty(b))
        if (missing.length > 0) {
            validationError = `Preencha os campos obrigatórios: ${missing.map((b) => b.label).join(', ')}`
            return false
        }
        validationError = ''
        return true
    }

    // Chamado pelo pai após upload para S3, substituindo base64 por URLs — evita re-upload em salvas subsequentes
    export function setAnswers(newAnswers: Record<string, any>) {
        answers = { ...newAnswers }
    }

    function onSaveDraft() {
        dispatch('save', { document: buildDocument('draft') })
    }

    function onSubmit() {
        if (!validate()) return
        dispatch('submit', { document: buildDocument('submitted') })
    }
</script>

<div class="form-fill">
    <div class="fill-sheet">
        {#if blocks.length === 0}
            <div class="fill-empty text-center">
                <p>Selecione um formulário na barra lateral para começar o preenchimento.</p>
            </div>
        {:else}
            <DocumentHeader header={headerFooter?.header} {companyId} />

            <form on:submit|preventDefault={onSubmit}>
                <div class="fill-grid">
                    {#each resolvedBlocks as block (block.id)}
                        <div class="fill-card" style="grid-column: span {getEffectiveBlockWidth(block)}">
                            <svelte:component
                                this={getBlockDefinition(block.type)?.viewComponent}
                                label={block.label}
                                data={block.data}
                                required={block.required}
                                readOnly={readOnlyAll || (isPublic && block.readOnlyForPublic)}
                                bind:value={answers[block.id]}
                                {formId}
                                blockId={block.id}
                            />
                        </div>
                    {/each}
                </div>

                {#if validationError}
                    <div class="fill-error">{validationError}</div>
                {/if}

                <DocumentFooter footer={headerFooter?.footer} />

                {#if !hideActions && !readOnlyAll}
                    <div class="fill-actions">
                        {#if allowDraft}
                            <button type="button" class="btn-ghost" on:click={onSaveDraft} disabled={saving}>
                                {saving ? 'Salvando...' : 'Salvar preenchimento'}
                            </button>
                        {/if}
                        <button type="submit" class="btn-primary" disabled={saving}>
                            {saving ? 'Salvando...' : signatureRequested ? 'Assinar' : 'Concluir preenchimento'}
                        </button>
                    </div>
                {:else if !hideActions && readOnlyAll && showSignButton}
                    <div class="fill-actions">
                        <button type="submit" class="btn-primary" disabled={saving}>
                            {saving ? 'Enviando...' : 'Assinar'}
                        </button>
                    </div>
                {/if}
            </form>
        {/if}
    </div>
</div>

<style>
    .form-fill {
        flex: 1;
        min-width: 0;
    }
    .fill-sheet {
        max-width: 1000px;
        margin: 0 auto;
        background: #edeef0;
        color: #212529;
        border-radius: 6px;
        padding: 16px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    }
    .fill-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 10px;
    }
    .fill-card {
        background: #fff;
        border: 1px solid #ecedf0;
        border-radius: 8px;
        padding: 16px 18px;
        min-width: 0;
        color: #212529;
    }
    /* sempre claro — a área de preenchimento não segue o tema escuro */
    :global(.theme_dark) .fill-card {
        background: #fff;
        color: #212529;
    }
    .fill-empty {
        color: #868e96;
        font-size: 0.9rem;
        padding: 24px 0;
    }
    .fill-empty p {
        margin: 0;
    }
    .fill-error {
        background: #fff5f5;
        border: 1px solid #ffc9c9;
        color: #c92a2a;
        border-radius: 8px;
        padding: 10px 14px;
        margin-bottom: 10px;
        font-size: 0.85rem;
    }
    .fill-actions {
        display: flex;
        gap: 10px;
        margin-top: 16px;
    }
    .btn-primary {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        border: none;
        border-radius: 8px;
        padding: 10px 20px;
        font-size: 0.9rem;
        font-weight: 500;
        background: #4263eb;
        color: #fff;
        cursor: pointer;
    }
    .btn-primary:hover:not(:disabled) {
        background: #364fc7;
    }
    .btn-primary:disabled {
        opacity: 0.6;
        cursor: default;
    }
    .btn-ghost {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        border: 1px solid #dee2e6;
        background: #fff;
        color: #495057;
        border-radius: 8px;
        padding: 8px 16px;
        font-size: 0.85rem;
        cursor: pointer;
    }
    .btn-ghost:hover:not(:disabled) {
        background: #f8f9fa;
    }
    .btn-ghost:disabled {
        opacity: 0.6;
        cursor: default;
    }

    @media (max-width: 700px) {
        .fill-grid {
            grid-template-columns: 1fr;
        }
        .fill-card {
            grid-column: 1 / -1 !important;
        }
    }
</style>
